import merge from 'lodash/merge';
import {colors, ColorShadesType} from '@yosmanyga/colores';
import type {ThemeVariablesType, ThemeBuildType} from '@yosmanyga/theme';

const variables: ThemeVariablesType = {
    mode: 'light',
    spacing: (value: number) => {
        return value * 8;
    },
    widthing: (value: number) => {
        return value;
    },
    font: (percent: string) => {
        return percent
    },
    palette: {
        brand1: colors['raw-umber'],
        brand2: colors['loulou'],
        neutral: colors['athens-gray'],
        positive: colors['emerald'],
        negative: colors['red-ribbon']
    },
    radius: 8,
};

const buildTheme = (
    variables: ThemeVariablesType | undefined,
    parent?: ThemeBuildType
): object => {
    const local = {
        text: buildText(variables as ThemeVariablesType),
        // anchor: buildAnchor(variables),
        button: buildButton(variables as ThemeVariablesType),
        dropdown: buildDropdown(variables as ThemeVariablesType),
        button_progress: buildButtonProgress(variables as ThemeVariablesType),
        input_wrapper: buildInputWrapper(variables as ThemeVariablesType),
        input_entry: buildInputEntry(variables as ThemeVariablesType),
        container: buildContainer(variables as ThemeVariablesType),
        progress: buildProgress(variables as ThemeVariablesType),
        icon: buildIcon(variables as ThemeVariablesType),
        line_placeholder: buildLinePlaceholder(variables as ThemeVariablesType),
        circle_placeholder: buildCirclePlaceholder(variables as ThemeVariablesType),
        // country_picker: buildCountryPicker(variables),
    };

    return merge(
        {},
        parent ? parent(variables) : {},
        local,
    )
};

const buildText = (variables: ThemeVariablesType) => {
    const {palette, font} = variables;

    return {
        color: palette.neutral['800'],
        font: {},
        secondary_intent: {
            color: palette.neutral['400'],
            font: {
                size: font('50%')
            }
        },
        title_intent: {
            font: {
                size: font('150%')
            }
        },
        banner_intent: {
            // color: palette.neutral['500'],
            font: {
                weight: 'bold',
            }
        },
        is_disabled: {
            color: palette.brand1['500'],
        },
        negative_intent: {
            color: palette.negative['500'],
        }
    };
};

// const buildAnchor = (variables?: ThemeVariablesType) => {
//     const button = buildButton(variables);
//
//     return {
//         ...button
//     };
// };
//

const buildButton = (variables: ThemeVariablesType) => {
    const {palette, radius} = variables;

    const text = buildText(variables);

    const common = (
        color: ColorShadesType
    ) => {
        return {
            color: color['0'],
            background: color['800'],
            border: {
                width: 1,
                color: color['900'],
                radius: radius,
            },
            pseudo: {
                hover: {
                    color: color['0'],
                    background: color['900'],
                }
            },
            is_disabled: {
                color: color['200'],
                background: color['600'],
                border: {
                    color: color['700'],
                },
                pseudo: {
                    hover: {
                        color: color['200'],
                        background: color['600'],
                    }
                },
            },
        }
    }

    return {
        font: {
            ...text?.font,
            size: '100%',
        },
        padding: 1,
        cursor: 'pointer',
        has_progress: {
            cursor: 'progress',
        },
        color: palette.brand1['800'],
        background: palette.neutral['100'],
        border: {
            width: 1,
            color: palette.neutral['400'],
            radius: radius,
        },
        pseudo: {
            hover: {
                color: palette.neutral['800'],
                background: palette.neutral['200'],
            }
        },
        is_disabled: {
            color: palette.neutral['500'],
            background: palette.neutral['100'],
            border: {
                color: palette.neutral['300'],
            },
            pseudo: {
                hover: {
                    color: palette.neutral['500'],
                    background: palette.neutral['50'],
                }
            },
        },
        primary_intent: {
            ...common(palette.brand1),
        },
        negative_intent: {
            ...common(palette.negative),
        },
        positive_intent: {
            ...common(palette.positive),
        },

        group_first_intent: {
            border: {
                radius: {
                    top: {
                        left: radius,
                        right: 0
                    },
                    bottom: {
                        left: radius,
                        right: 0
                    }
                }
            }
        },
        group_middle_intent: {
            border: {
                left: 0,
                radius: 0
            }
        },
        group_last_intent: {
            border: {
                left: 0,
                radius: {
                    top: {
                        left: 0,
                        right: radius
                    },
                    bottom: {
                        left: 0,
                        right: radius
                    }
                }
            }
        }
    };
};

const buildDropdown = (variables: ThemeVariablesType) => {
    const inputWrapper = buildInputWrapper(variables);

    return {
        border: inputWrapper.border,
        padding: 1,
    }
}

const buildContainer = (variables: ThemeVariablesType) => {
    const {palette, radius} = variables;

    return {
        layout_intent: {
            // background: neutral_base
        },
        // modal_body_intent: {
        //     flow: 'column',
        //     margin: {
        //         left: 4,
        //         right: 4,
        //         top: 4,
        //     },
        //     padding: 2,
        //     background: palette.primary.right,
        //     border: {
        //         radius: 4,
        //     },
        // },
        // menu_title_intent: {
        //     flow: 'column',
        //     align: {
        //         cross: 'center',
        //     }
        // }
    };
};

const buildButtonProgress = (variables: ThemeVariablesType) => {
    const button = buildButton(variables);

    return {
        color: button.color
    };
};

const buildInputWrapper = (variables: ThemeVariablesType) => {
    const {palette} = variables;

    const button = buildButton(variables);

    return {
        border: {
            ...button.border,
            color: palette.neutral['600'],
        }
    }
}

const buildInputEntry = (variables: ThemeVariablesType) => {
    const text = buildText(variables);
    const button = buildButton(variables);

    return {
        font: text.font,
        color: button.color,
        background: 'transparent',
        line: 1.5,
        is_multi: {
            line: 'inherit',
        },
        padding: 1.4, // Minimum to fit "end icon" inside without changing height
    };
};

const buildProgress = (variables: ThemeVariablesType) => {
    const button = buildButton(variables);

    return {
        color: button.color
    };
};

const buildIcon = (variables: ThemeVariablesType) => {
    const {palette} = variables;

    const text = buildText(variables);

    return {
        color: text.color,
    };
};

const buildLinePlaceholder = (variables: ThemeVariablesType) => {
    const {palette} = variables;

    const text = buildText(variables);

    return {
        color: palette.neutral['200'],
        background: palette.neutral['100'],
    };
};

const buildCirclePlaceholder = (variables: ThemeVariablesType) => {
    const {palette} = variables;

    const text = buildText(variables);

    return {
        color: palette.neutral['200'],
        background: palette.neutral['100'],
    };
};

// const buildCountryPicker = (variables: ThemeVariablesType) => {
//     const inputWrapper = buildInputWrapper(variables);
//
//     return {
//         country_intent: {
//             ...inputWrapper
//         }
//     }
// };

type LayoutType = {
    background: number,
    foreground: number
};

type SurfaceType = LayoutType & {
    border: number
}

type ButtonType = SurfaceType

type IndexesType = {
    layout: LayoutType,
    surface: SurfaceType,
    button: ButtonType
}

export {
    variables,
    // resolveThemeIndexes,
    buildTheme
}